import { useEffect, useState } from 'react'

import { navigate, routes, useParams } from '@redwoodjs/router'
import { MetaTags } from '@redwoodjs/web'

import { SectionInfoIcon } from 'src/components/Feedback/SectionInfoIcon/SectionInfoIcon'
import { SearchBox } from 'src/components/Inputs/SearchBox/SearchBox'

import CarrierContactHistoryPanel from './components/CarrierContactHistoryPanel'
import CarrierContactsPanel from './components/CarrierContactsPanel'
import CarrierDetailPanel from './components/CarrierDetailPanel'
import CarrierSearchResults from './components/CarrierSearchResults'
import {
  MINIMUM_SEARCH_CHARACTERS,
  useCarrierSearch,
} from './hooks/useCarrierSearch'

const PAGE_DESCRIPTION =
  'Look up an employer in the global directory by DOT number or name. This directory is shared by every company, so what you see here is what every verification request is sent against.'

const AdminVoECarriersPage = () => {
  // Present only on the /admin/voe/carriers/:carrierId route.
  const { carrierId } = useParams()
  const [searchInput, setSearchInput] = useState('')

  // All three panels read the same rows from different angles, so a change in
  // one is news to the others: the filter points the history at a contact
  // picked in the table, and the counter tells the history and the carrier's
  // own contact count to read again after an edit or an archive.
  const [historyContactId, setHistoryContactId] = useState<string | null>(null)
  const [contactsChanged, setContactsChanged] = useState(0)

  // The panels below are remounted by their key when the carrier changes, but
  // this state is not — and a contact id from the carrier just left belongs to
  // nobody here. Left alone, the new carrier's history reads as empty while
  // claiming to be filtered.
  useEffect(() => setHistoryContactId(null), [carrierId])

  const { term, awaitingInput, results, totalCount, loading, error } =
    useCarrierSearch(searchInput)

  const openCarrier = (id: string) =>
    navigate(routes.adminVoeCarrier({ carrierId: id }))

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-6">
      <MetaTags
        title="Employment Verification Carriers"
        description="Admin VoE carrier directory"
      />

      {carrierId ? (
        // On a carrier's own page the heading belongs to the carrier — the
        // section nav is the way back, so no second title and no back button.
        //
        // Siblings rather than nested: both key off the same carrierId, so
        // nesting the contacts inside the carrier view would make their query
        // wait for the carrier's to resolve first.
        // Keyed by carrier: without it, moving from one carrier to another
        // keeps the open edit form mounted with the previous carrier's values,
        // and saving writes them onto the carrier now on screen.
        <div key={carrierId} className="flex flex-col gap-5">
          <CarrierDetailPanel carrierId={carrierId} reloadKey={contactsChanged} />
          <CarrierContactsPanel
            carrierId={carrierId}
            onViewHistory={setHistoryContactId}
            onContactsChanged={() => setContactsChanged((count) => count + 1)}
          />
          <CarrierContactHistoryPanel
            carrierId={carrierId}
            contactId={historyContactId}
            onClearFilter={() => setHistoryContactId(null)}
            reloadKey={contactsChanged}
          />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5">
            <h1 className="text-lg font-semibold text-doubleNickel-gray-900">
              Carriers
            </h1>
            <SectionInfoIcon
              tooltip={PAGE_DESCRIPTION}
              ariaLabel="About the carrier directory"
            />
          </div>

          <SearchBox
            className="max-w-md"
            value={searchInput}
            setValue={setSearchInput}
            // The search is live off the debounced value, so there is no
            // separate submit — Enter and the clear button write the box's own
            // value back through the same path.
            onSearch={setSearchInput}
            placeholder="Search by DOT number or name"
            ariaLabel="Search carriers by DOT number or name"
          />

          <CarrierSearchResults
            term={term}
            results={results}
            totalCount={totalCount}
            loading={loading}
            error={error}
            awaitingInput={awaitingInput}
            minimumCharacters={MINIMUM_SEARCH_CHARACTERS}
            onSelect={openCarrier}
          />
        </>
      )}
    </div>
  )
}

export default AdminVoECarriersPage
